import { ErrorResponse, Notification, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';

export type NotificationState = {
  data: Notification[];
  isLoading: boolean;
  error: ErrorResponse | null;
  unreadCount: number;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;
};

export type NotificationActions = {
  fetchData: () => Promise<ServiceResponse<Notification[]>>;
  loadMore: () => Promise<ServiceResponse<Notification[]>>;
  markAsRead: (ids: string[]) => Promise<ServiceResponse<unknown>>;
  setUnreadCount: (count: number) => void;
  setPageSize: (size: number) => void;
  clear: () => void;
};

export type NotificationStore = NotificationState & NotificationActions;

export const useNotificationStore = create<NotificationStore>((set, get) => {
  return {
    data: [],
    isLoading: false,
    error: null,
    unreadCount: 0,
    hasMore: true,
    currentPage: 0,
    pageSize: 25,

    // Actions
    fetchData: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<Notification[]>;
      }

      const localeId = useLocaleStore.getState().localeId;
      const { pageSize } = get();

      set({ isLoading: true, error: null, currentPage: 0, hasMore: true, data: [] });
      const response = await bzbsService.notificationApi.notifications({
        mode: 'all',
        sortBy: 'createdate_desc',
        top: pageSize,
        skip: 0,
        options: { device_locale: localeId },
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        const unreadCount = response.model.filter((n) => n.IsRead == false).length;
        set({
          data: response.model,
          unreadCount,
          hasMore: response.model.length === pageSize,
          currentPage: 1,
          error: null,
        });
      } else {
        set({ error: response });
      }

      return response;
    },

    loadMore: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<Notification[]>;
      }

      const { hasMore, currentPage, pageSize, data } = get();
      const localeId = useLocaleStore.getState().localeId;

      if (!hasMore) {
        return { type: 'client-error', message: 'No more data available' } as ServiceResponse<Notification[]>;
      }

      set({ isLoading: true, error: null });
      const response = await bzbsService.notificationApi.notifications({
        mode: 'all',
        sortBy: 'createdate_desc',
        top: pageSize,
        skip: currentPage * pageSize,
        options: { device_locale: localeId },
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        const merged = [...data, ...response.model];
        const unreadCount = merged.filter((n) => n.IsRead == false).length;
        set({
          data: merged,
          unreadCount,
          hasMore: response.model.length === pageSize,
          currentPage: currentPage + 1,
          error: null,
        });
      } else {
        set({ error: response });
      }

      return response;
    },

    markAsRead: async (ids: string[]) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      const response = await bzbsService.notificationApi.read({
        ids: ids.join(','),
        options: { device_locale: useLocaleStore.getState().localeId },
      });
      if (response.type === 'success') {
        set({ data: get().data.map((n) => (ids.includes(n.RowKey || '') ? { ...n, IsRead: true } : n)) });
        const unreadCount = get().data.filter((n) => n.IsRead == false).length;
        set({ unreadCount });
      } else {
        set({ error: response });
      }

      return response;
    },

    setUnreadCount: (count: number) => {
      set({ unreadCount: count });
    },

    setPageSize: (size: number) => {
      const next = size > 0 ? size : 25;
      set({ pageSize: next });
    },

    clear: () => {
      set({
        data: [],
        isLoading: false,
        error: null,
        unreadCount: 0,
        hasMore: true,
        currentPage: 0,
      });
    },
  };
});
